import React, { useEffect, useState } from 'react';
import ProductIdentifierSection from './ProductIdentifierSection';
import ProductDescriptionSection from './ProductDescriptionSection'; // Import the new component
import ProductBrochureSection, { BrochureParagraph } from './ProductBrochureSection';
import DosingSection from './DosingSection';
import IngredientsSection, { Ingredients } from './IngredientsSection';
import './style.css';
import { GraphQLClient } from 'graphql-hooks';
import { useSearchParams } from 'react-router-dom';
import useGQLQueryWithClient from '../../hooks/useGQLQueryWithClient';
import { WebshopItemForTranslateResponse, getWebshopItemForTranslate } from './queries';
import updateProduct from './updateProducts';

const cmaKey = "96fc787908f9f261dc3f9dd89893de"; // TODO Config

const MainForm: React.FC = () => {
  const [queryParameters] = useSearchParams();

  const productId = queryParameters.get("id");
  const language = queryParameters.get("language") ?? "en";
  const referenceLanguage = queryParameters.get("reference") ?? "en";
  
  const client = new GraphQLClient({
    url: "https://graphql.datocms.com/",
    headers: {
      "Authorization": `Bearer c2637bf4c3ca322961249cf1b65310`, // Todo use user token
      "X-Include-Drafts": "true",
    }
  });

  const { loading, error, data } = useGQLQueryWithClient(client, getWebshopItemForTranslate(productId ?? "", language));
  const { loading: referenceLoading, error: referenceError, data: referenceData } = useGQLQueryWithClient(client, getWebshopItemForTranslate(productId ?? "", referenceLanguage));

  const [showReference, setShowReference] = useState(false);

  const [productName, setProductName] = useState('');
  const [shortTitle, setShortTitle] = useState('');
  const [shortDescription, setShortDescription] = useState('');
  const [entrustment, setEntrustment] = useState('');
  const [whatIsTitle, setWhatIsTitle] = useState('');
  const [paragraphs, setParagraphs] = useState('');
  const [brochureParagraphs, setBrochureParagraphs] = useState<BrochureParagraph[]>([]);
  const [dosingText, setDosingText] = useState('');
  const [dosing, setDosing] = useState('');
  const [ingredients, setIngredients] = useState<Ingredients>([]);

  const [referenceProductName, setReferenceProductName] = useState('');
  const [referenceShortTitle, setReferenceShortTitle] = useState('');
  const [referenceShortDescription, setReferenceShortDescription] = useState('');
  const [referenceEntrustment, setReferenceEntrustment] = useState('');
  const [referenceWhatIsTitle, setReferenceWhatIsTitle] = useState('');
  const [referenceParagraphs, setReferenceParagraphs] = useState('');
  const [referenceBrochureParagraphs, setReferenceBrochureParagraphs] = useState<BrochureParagraph[]>([]);
  const [referenceDosingText, setReferenceDosingText] = useState('');
  const [referenceDosing, setReferenceDosing] = useState('');
  const [referenceIngredients, setReferenceIngredients] = useState<Ingredients>([]);

  useEffect(() => {
    if (!loading && !error) {
      console.log(data)
      const translateDate : WebshopItemForTranslateResponse = (data.data as any).productPage;
      setProductName(translateDate.productName);
      setShortTitle(translateDate.title);
      setShortDescription(translateDate.subtitle);
      setEntrustment(translateDate.preface);
      setWhatIsTitle(translateDate.whatIsTitle);
      setParagraphs(translateDate.whatIsParagraphs.map(a => a.text).join("\n"));
      setBrochureParagraphs(translateDate.marketingInfo);
      setDosingText(translateDate.dosingText);
      setDosing(translateDate.dosingParagraphs.map(a => a.text).join("\n"));
      setIngredients(translateDate.ingredients);
    }
  }, [loading, error, data]);

  useEffect(() => {
    if (!referenceLoading && !referenceError) {
      const translateDate : WebshopItemForTranslateResponse = (referenceData.data as any).productPage;
      setReferenceProductName(translateDate.productName);
      setReferenceShortTitle(translateDate.title);
      setReferenceShortDescription(translateDate.subtitle);
      setReferenceEntrustment(translateDate.preface);
      setReferenceWhatIsTitle(translateDate.whatIsTitle);
      setReferenceParagraphs(translateDate.whatIsParagraphs.map(a => a.text).join("\n"));
      setReferenceBrochureParagraphs(translateDate.marketingInfo);
      setReferenceDosingText(translateDate.dosingText);
      setReferenceDosing(translateDate.dosingParagraphs.map(a => a.text).join("\n"));
      setReferenceIngredients(translateDate.ingredients);
    }
  }, [referenceLoading, referenceError, referenceData]);

  if (loading || referenceLoading) return <>Loading data</>
  if (error) return <>Some error occured</>
  if (referenceError) return <>Some error occured during loading the reference texts</>

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const productData : WebshopItemForTranslateResponse = {
      productName,
      title: shortTitle,
      subtitle: shortDescription,
      preface: entrustment,
      whatIsTitle, 
      whatIsParagraphs: paragraphs.split("\n").filter(e => e.length > 0).map(e => {return {text: e}}),
      marketingInfo: brochureParagraphs,
      dosingParagraphs: dosing.split("\n").filter(e => e.length > 0).map(e => {return {text: e}}),
      dosingText,
      ingredients,
    };
    if (productId === null) return;
    updateProduct(cmaKey, productId, language, productData)
      .then((res) => alert("Successfully saved")).
      catch(e => {
        console.log("Errors:")
        if (e.message.includes("STALE_ITEM_VERSION")) {
          alert("Couldn't save your modifications, because someone else also saved some data in the exact moment. Please save again.");
        } else {
          alert(`Generic error: ${e}`);
        }
      });
  };

  return (<>
    <div className='fixed-top'>
      You can check the rendered product page using the following link:<br/>
      <a href={`/webshop/viewer/item?id=${productId}&language=${language}`} target="_blank">Rendered product page</a>
      <br/>
      <br/>
      <button onClick={(e) => setShowReference((e) => !e)}>
        {!showReference && <>Switch to reference text</>}
        {showReference && <>Switch to original text</>}
      </button>
      <br/>
      <br/>
    </div>

    <div className='form-container'>
      <form onSubmit={handleSubmit}>
        <ProductIdentifierSection
          productName={productName}
          language={showReference?referenceLanguage:language}
        />
        <ProductDescriptionSection
          shortTitle={showReference?referenceShortTitle:shortTitle}
          setShortTitle={showReference?(e)=>{}:setShortTitle}
          shortDescription={showReference?referenceShortDescription:shortDescription}
          setShortDescription={showReference?(e)=>{}:setShortDescription}
          entrustment={showReference?referenceEntrustment:entrustment}
          setEntrustment={showReference?(e)=>{}:setEntrustment}
          paragraphs={showReference?referenceParagraphs:paragraphs}
          setParagraphs={showReference?(e)=>{}:setParagraphs}
        />
        <ProductBrochureSection 
          brochureParagraphs={showReference?referenceBrochureParagraphs:brochureParagraphs} 
          setBrochureParagraphs={showReference?(e)=>{}:setBrochureParagraphs}
          mutableParagraphs={!showReference}
        />
        <DosingSection dosing={showReference?referenceDosing:dosing} setDosing={showReference?(e)=>{}:setDosing} />
        <IngredientsSection ingredients={showReference?referenceIngredients:ingredients} setIngredients={showReference?(e)=>{}:setIngredients} />
        <br/>
        <br/>
        <hr/>
        <div style={{textAlign: "center"}}>
            {!showReference && <button type="submit">Submit</button>}
            {showReference && <>Switch back to original language to save</>}
        </div>
      </form>
    </div>
  </>);
};

export default MainForm;
